// This source code is owned by Avalara Inc. Unauthorized copying of this file,
// via any medium, is strictly prohibited. Proprietary and confidential.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  buildDynamicReportsTour,
  getTransactionCategoryId,
  getTourReportId
} from '../utils/tourGuid/drsTourGuide';
import Logger, { LOG_LEVELS } from '../utils/logger';
import useBlocker from './useBlocker';

const HOOK_NAME = 'useDynamicReportsTourGuide';
/** @type {Logger} */
const log = new Logger(HOOK_NAME, LOG_LEVELS.DEBUG);

const STORAGE_TOUR_STARTED_VALUE = 'true';
const UNKNOWN_USER_NAME = 'Unknown';
const AUTO_START_DELAY_MS = 100;

/**
 * Custom hook for managing Dynamic Reports tour guide functionality
 * 
 * This hook encapsulates all tour guide logic including:
 * - Tour initialization and configuration
 * - Auto-start functionality
 * - Tour status monitoring
 * - Navigation blocking integration
 * - Storage management
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.isGuidedTourEnabled - Whether the tour guide feature is enabled
 * @param {string} options.userName - Current user name
 * @param {boolean} options.loading - Whether data is currently loading
 * @param {boolean} options.loadingSpec - Whether specifications are loading
 * @param {Array} options.categories - Report categories from specifications
 * @param {Object} [options.history] - React Router history object (optional, for navigation blocking)
 * 
 * @returns {Object} Tour guide API
 * @returns {Object} returns.guidedToursRef - Ref to attach to XGuidedTours component
 * @returns {Array} returns.guidedTours - Tour configuration array
 * @returns {string} returns.guidedTourId - ID of the guided tour
 * @returns {string} returns.transactionCategoryId - Transaction category ID for tour triggers
 * @returns {string} returns.tourReportId - Tour report ID (invoiceLines if present, else changesLogV2)
 * @returns {Function} returns.startTour - Function to manually start the tour
 */
export default function useDynamicReportsTourGuide({
  isGuidedTourEnabled,
  userName,
  loading,
  loadingSpec,
  categories,
  history
}) {
  // Refs for tour management
  const guidedToursRef = useRef(null); // Ref to XGuidedTours component instance
  const tourRef = useRef(null); // Cached tour instance for synchronous status checks
  const isTourActiveRef = useRef(false); // Whether tour is active (for blocker predicate)

  // Refs for auto-start tracking
  const autoStartAttemptedRef = useRef(false); // Prevents multiple auto-start attempts
  const specLoadStartedRef = useRef(false); // Tracks when specification loading has begun
  const readyCheckTimeoutRef = useRef(null); // Timeout for delayed ready check

  // Set up navigation blocker - only block when tour is enabled and active
  const blocker = useBlocker(history, ({ action }) => {
    return action === 'POP' && isGuidedTourEnabled && isTourActiveRef.current;
  });

  // Memoized category and report IDs used for tour step triggers
  const transactionCategoryId = useMemo(
    () => getTransactionCategoryId(categories),
    [categories]
  );

  const tourReportId = useMemo(
    () => getTourReportId(categories, transactionCategoryId),
    [categories, transactionCategoryId]
  );

  // Tour config depends on tour report (submit step selector, etc.)
  const guidedTours = useMemo(
    () => buildDynamicReportsTour(categories, transactionCategoryId),
    [categories, transactionCategoryId]
  );
  const guidedTourId = guidedTours?.[0]?.id;

  /**
   * Get storage key for tracking if tour has been started for a user
   * @param {string} name - User name
   * @returns {string} Storage key
   */
  const getTourStorageKey = useCallback(
    (name) => `drs-tour-started:${encodeURIComponent(name)}`,
    []
  );

  /**
   * Mark tour as started in localStorage to prevent auto-start on subsequent visits
   * @param {string} storageKey - Storage key for the user's tour status
   */
  const markTourStarted = useCallback((storageKey) => {
    try {
      localStorage.setItem(storageKey, STORAGE_TOUR_STARTED_VALUE);
    } catch (e) {
      // Log but don't throw - storage errors shouldn't break the tour functionality
      log.debug('Failed to mark tour as started in localStorage', {
        errorMessage: e?.message
      });
    }
  }, []);

  /**
   * Get the tour instance (similar to useGuidedTour.ts getTour)
   * @returns {Promise<Object|null>} Promise that resolves to the tour instance or null
   */
  const getTour = useCallback(async () => {
    if (!guidedToursRef.current || !guidedTourId) {
      return null;
    }
    try {
      return await guidedToursRef.current.getGuidedTour(guidedTourId);
    } catch (error) {
      log.debug('getTour: error getting tour instance', {
        errorMessage: error?.message,
        guidedTourId
      });
      return null;
    }
  }, [guidedTourId]);

  /**
   * Update tourRef and isTourActiveRef from the actual tour instance.
   * This is the only place that updates tourRef.current (and isTourActiveRef.current for tour status).
   */
  const updateTourRef = useCallback(async () => {
    const tour = await getTour();
    tourRef.current = tour;
    if (tour && typeof tour.isActive === 'function') {
      isTourActiveRef.current = tour.isActive();
    } else {
      isTourActiveRef.current = false;
    }
  }, [getTour]);

  const handleTourExit = useCallback(async () => {
    const tour = await getTour();
    if (!tour?.isActive?.()) {
      return true;
    }
    try {
      await tour?.cancel?.();
      await new Promise((resolve) => setTimeout(resolve, 0));
      if (tour?.isActive?.()) {
        return false;
      }
      tour?.hide?.();
      return true;
    } catch (error) {
      log.warn('Failed to exit guided tour', { errorMessage: error?.message });
      return false;
    }
  }, [getTour]);

  /**
   * Manually start the guided tour
   * Marks tour as started in localStorage and notifies parent component
   */
  const startTour = useCallback(async () => {
    if (!guidedTourId) {
      log.debug('startTour: No guided tour ID available');
      return;
    }

    // Prevent starting tour while data is still loading
    if (loading || loadingSpec) {
      log.debug('startTour: Cannot start tour while page is loading', {
        loading,
        loadingSpec
      });
      return;
    }

    // Mark tour as started for this user to prevent auto-start on next visit
    if (userName && userName !== UNKNOWN_USER_NAME) {
      markTourStarted(getTourStorageKey(userName));
    }

    try {
      await guidedToursRef.current?.startGuidedTour(guidedTourId);
      await updateTourRef(); // Update cached tour reference
    } catch (error) {
      log.debug('startTour: Error starting tour', {
        errorMessage: error?.message,
        guidedTourId
      });
    }
  }, [guidedTourId, userName, loading, loadingSpec, getTourStorageKey, markTourStarted, updateTourRef]);

  // Automatically handle blocked navigation when blocker state changes
  useEffect(() => {
    if (blocker.state === 'blocked') {
      const handleBlockedNavigation = async () => {
        const shouldNavigate = await handleTourExit();
        if (shouldNavigate) {
          blocker.proceed();
        } else {
          blocker.reset();
        }
      };
      handleBlockedNavigation().catch((err) => {
        log.warn('Error handling blocked navigation', { errorMessage: err?.message });
        blocker.reset();
      });
    }
  }, [blocker, handleTourExit]);

  /**
   * Auto-start tour on first load for new users
   * Only runs once per user (tracked via localStorage)
   * Waits for all data to be loaded and page to be stable before starting
   */
  useEffect(() => {
    // Clear any pending timeout on effect re-run
    if (readyCheckTimeoutRef.current) {
      clearTimeout(readyCheckTimeoutRef.current);
      readyCheckTimeoutRef.current = null;
    }

    // Early exit: skip autostart when any prerequisite is not met
    const canAutostart =
      isGuidedTourEnabled &&
      guidedTourId &&
      userName &&
      userName !== UNKNOWN_USER_NAME &&
      !loading &&
      !loadingSpec &&
      specLoadStartedRef.current &&
      categories?.length > 0 &&
      guidedToursRef.current?.startGuidedTour;

    if (!canAutostart) {
      return;
    }

    const storageKey = getTourStorageKey(userName);
    let isMounted = true;

    /**
     * Verify all conditions are met before attempting to start tour
     * @returns {boolean} True if ready to start, false otherwise
     */
    const isReadyToStart = () => {
      return (
        specLoadStartedRef.current &&
        !loadingSpec &&
        !loading &&
        categories &&
        categories.length > 0 &&
        guidedToursRef.current?.startGuidedTour &&
        isMounted
      );
    };

    /**
     * Check if tour has already been auto-started for this user
     * @returns {boolean} True if tour was previously started
     */
    const hasAutoStarted = () => localStorage.getItem(storageKey) === STORAGE_TOUR_STARTED_VALUE;

    /**
     * Attempt to auto-start the tour
     * Only runs if tour hasn't been started before and all conditions are met
     */
    const tryStart = async () => {
      // Final verification before starting
      if (!isReadyToStart()) {
        log.debug('Tour guide: Not ready to start tour', {
          specLoadStarted: specLoadStartedRef.current,
          loadingSpec,
          loading,
          hasRef: !!guidedToursRef.current?.startGuidedTour
        });
        return;
      }

      // Only auto-start if user hasn't seen the tour before
      if (!hasAutoStarted() && !autoStartAttemptedRef.current) {
        autoStartAttemptedRef.current = true;
        markTourStarted(storageKey);
        log.debug('Tour guide: Auto-starting tour', { userName, guidedTourId, storageKey });

        try {
          await guidedToursRef.current.startGuidedTour(guidedTourId);
          await updateTourRef();
        } catch (e) {
          log.debug('Tour guide: Error auto-starting tour', {
            errorMessage: e?.message,
            guidedTourId
          });
          // Reset flag on error to allow retry on next render
          autoStartAttemptedRef.current = false;
        }
      }
    };

    // Add a small delay to ensure page is fully stable before starting tour
    // This prevents the tour from starting during page transitions or re-renders
    const timeoutId = setTimeout(() => {
      // Verify this is still the active timeout (prevent stale callbacks)
      if (readyCheckTimeoutRef.current === timeoutId && isReadyToStart()) {
        tryStart();
      }
    }, AUTO_START_DELAY_MS);
    readyCheckTimeoutRef.current = timeoutId;

    // Cleanup: clear timeout and mark component as unmounted
    return () => {
      isMounted = false;
      if (readyCheckTimeoutRef.current) {
        clearTimeout(readyCheckTimeoutRef.current);
        readyCheckTimeoutRef.current = null;
      }
    };
  }, [
    guidedTourId,
    userName,
    loading,
    loadingSpec,
    categories,
    isGuidedTourEnabled,
    getTourStorageKey,
    markTourStarted,
    updateTourRef
  ]);

  /**
   * Mark that specification loading has started
   * Must be called by the parent component when specification loading begins
   * This allows the auto-start effect to wait for specs before starting the tour
   */
  const markSpecLoadStarted = useCallback(() => {
    specLoadStartedRef.current = true;
  }, []);

  return {
    // Tour configuration and refs
    guidedToursRef,              // Ref to attach to XGuidedTours component
    guidedTours,                 // Tour configuration array
    guidedTourId,                // ID of the guided tour

    // Tour trigger IDs (for tour step targeting)
    transactionCategoryId,       // Transaction category ID for tour triggers
    tourReportId,                // Tour report ID (invoiceLines if present, else changesLogV2)

    // Tour control functions
    startTour,                   // Manually start the tour

    // Lifecycle
    markSpecLoadStarted
  };
}
